import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { USER_ROLES } from 'constants/common';

import { userSelector } from 'store/auth/selectors';

import * as api from 'api/methods';

import TimelineSection from './TimelineSection';

import {
  EMPTY_SESSIONS_RESPONSE,
  JOURNEY_HIGHLIGHT_DURATION_MS,
  PLATFORM_FILTER_ALL,
  SESSION_DETAIL_ELEMENT_ID,
  SESSION_DETAIL_FETCH_DELAY_MS,
  SESSIONS_LIMIT,
  buildExitScreenStats,
  buildJourneySummary,
  filterSessionsByPlatform,
  filterVisibleSessions,
  getAdjacentSessionId,
  getJourneyHighlightElementId,
  getJourneyNavigationDirection,
  getPlatformTabs,
  getSessionsDateRange,
  groupSessionsByDay,
  isEditableEventTarget,
  normalizeSessionDetail,
  normalizeSessionsResponse,
  type AnalyticsStudentSessionDetailDto,
  type AnalyticsStudentSessionsDto,
  type ExitCause,
  type JourneyHighlight,
  type PlatformFilter,
} from './TimelineSection.helpers';
import { buildStudentJourneyStory } from './journeySessionStory';
import { readJourneyTechMode, writeJourneyTechMode } from './journeyTechMode';

const scrollElementIntoView = (
  elementId: string,
  block: ScrollLogicalPosition,
) => {
  document
    .getElementById(elementId)
    ?.scrollIntoView({ behavior: 'smooth', block });
};

const TimelineSectionContainer = ({
  studentEmail,
}: {
  studentEmail?: string | null;
}) => {
  const { id: studentId } = useParams();
  const user = useSelector(userSelector);
  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;
  const { i18n } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sessionsResponse, setSessionsResponse] =
    useState<AnalyticsStudentSessionsDto>(EMPTY_SESSIONS_RESPONSE);
  const [platform, setPlatform] = useState<PlatformFilter>(PLATFORM_FILTER_ALL);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [techMode, setTechMode] = useState(readJourneyTechMode);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const [detailsById, setDetailsById] = useState<
    Record<string, AnalyticsStudentSessionDetailDto>
  >({});
  const [failedDetailIds, setFailedDetailIds] = useState<
    Record<string, boolean>
  >({});
  const [highlight, setHighlight] = useState<JourneyHighlight | null>(null);
  const dateRangeRef = useRef(getSessionsDateRange(new Date()));
  const requestGenerationRef = useRef(0);
  const pendingDetailIdsRef = useRef(new Set<string>());
  const highlightTimeoutRef = useRef<number | null>(null);

  const platformSessions = useMemo(
    () => filterSessionsByPlatform(sessionsResponse.sessions, platform),
    [sessionsResponse.sessions, platform],
  );
  const visibleSessions = useMemo(
    () =>
      filterVisibleSessions(sessionsResponse.sessions, platform, onlyProblems),
    [sessionsResponse.sessions, platform, onlyProblems],
  );
  const activeSessionId = visibleSessions.some(
    (session) => session.id === selectedSessionId,
  )
    ? selectedSessionId
    : visibleSessions[0]?.id ?? null;
  const activeSession =
    visibleSessions.find((session) => session.id === activeSessionId) ?? null;
  const activeDetail = activeSessionId
    ? detailsById[activeSessionId] ?? null
    : null;
  const isDetailFailed = Boolean(
    activeSessionId && failedDetailIds[activeSessionId],
  );
  const summary = useMemo(
    () => buildJourneySummary(platformSessions),
    [platformSessions],
  );
  const exitScreenStats = useMemo(
    () => buildExitScreenStats(platformSessions),
    [platformSessions],
  );
  /* The platform mix only means something when both platforms are shown. */
  const studentStory = useMemo(
    () =>
      buildStudentJourneyStory(platformSessions, {
        showPlatform: platform === PLATFORM_FILTER_ALL,
      }),
    [platformSessions, platform, i18n.language],
  );
  const platformTabs = useMemo(
    () => getPlatformTabs(sessionsResponse.platformCounts),
    [sessionsResponse.platformCounts, i18n.language],
  );
  const sessionGroups = useMemo(
    () => groupSessionsByDay(visibleSessions, i18n.language),
    [visibleSessions, i18n.language],
  );

  useEffect(() => {
    requestGenerationRef.current += 1;
    pendingDetailIdsRef.current.clear();
    setSessionsResponse(EMPTY_SESSIONS_RESPONSE);
    setSelectedSessionId(null);
    setDetailsById({});
    setFailedDetailIds({});
    setLoadError(false);

    if (!isRootAdmin || !studentId) {
      setLoading(false);
      return;
    }

    const requestGeneration = requestGenerationRef.current;
    const dateRange = getSessionsDateRange(new Date());

    dateRangeRef.current = dateRange;

    const loadSessions = async () => {
      setLoading(true);

      try {
        const response = await api.getStudentAnalyticsSessions(studentId, {
          ...dateRange,
          limit: SESSIONS_LIMIT,
        });

        if (requestGeneration === requestGenerationRef.current) {
          setSessionsResponse(normalizeSessionsResponse(response));
        }
      } catch {
        if (requestGeneration === requestGenerationRef.current) {
          setLoadError(true);
        }
      } finally {
        if (requestGeneration === requestGenerationRef.current) {
          setLoading(false);
        }
      }
    };

    void loadSessions();
  }, [isRootAdmin, studentId]);

  useEffect(() => {
    if (
      !studentId ||
      !activeSessionId ||
      detailsById[activeSessionId] ||
      failedDetailIds[activeSessionId] ||
      pendingDetailIdsRef.current.has(activeSessionId)
    ) {
      return;
    }

    const sessionId = activeSessionId;
    const requestGeneration = requestGenerationRef.current;

    const loadSessionDetail = async () => {
      pendingDetailIdsRef.current.add(sessionId);

      try {
        const response = await api.getStudentAnalyticsSession(
          studentId,
          sessionId,
          dateRangeRef.current,
        );

        if (requestGeneration === requestGenerationRef.current) {
          setDetailsById((previousDetails) => ({
            ...previousDetails,
            [sessionId]: normalizeSessionDetail(response),
          }));
        }
      } catch {
        if (requestGeneration === requestGenerationRef.current) {
          setFailedDetailIds((previousFailures) => ({
            ...previousFailures,
            [sessionId]: true,
          }));
        }
      } finally {
        pendingDetailIdsRef.current.delete(sessionId);
      }
    };

    // Rapid selection changes (held J/K) reset the timer, so only the settled session is fetched.
    const detailFetchTimeout = window.setTimeout(
      loadSessionDetail,
      SESSION_DETAIL_FETCH_DELAY_MS,
    );

    return () => window.clearTimeout(detailFetchTimeout);
  }, [studentId, activeSessionId, detailsById, failedDetailIds]);

  useEffect(() => {
    if (!isRootAdmin) {
      return;
    }

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        isEditableEventTarget(event.target)
      ) {
        return;
      }

      const direction = getJourneyNavigationDirection(event.key);

      if (!direction) {
        return;
      }

      const nextSessionId = getAdjacentSessionId(
        visibleSessions,
        activeSessionId,
        direction,
      );

      if (nextSessionId && nextSessionId !== activeSessionId) {
        event.preventDefault();
        setSelectedSessionId(nextSessionId);
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [isRootAdmin, visibleSessions, activeSessionId]);

  useEffect(() => {
    setHighlight(null);
  }, [activeSessionId]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current != null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    },
    [],
  );

  const focusJourneyElement = (target: JourneyHighlight) => {
    if (highlightTimeoutRef.current != null) {
      window.clearTimeout(highlightTimeoutRef.current);
    }

    setHighlight(null);

    window.requestAnimationFrame(() => {
      scrollElementIntoView(getJourneyHighlightElementId(target), 'center');
      setHighlight(target);
      highlightTimeoutRef.current = window.setTimeout(
        () => setHighlight(null),
        JOURNEY_HIGHLIGHT_DURATION_MS,
      );
    });
  };

  const handlePlatformChange = (value: string) => {
    setPlatform(value as PlatformFilter);
  };

  const handleOnlyProblemsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOnlyProblems(event.target.checked);
  };

  const handleTechModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTechMode(event.target.checked);
    writeJourneyTechMode(event.target.checked);
  };

  // Stable reference keeps memoized list rows from re-rendering on every selection.
  const handleSessionSelect = useCallback((sessionId: string) => {
    setSelectedSessionId(sessionId);
  }, []);

  const handleCauseSessionShow = (cause: ExitCause) => {
    const sessionId =
      cause === 'error'
        ? summary.latestErrorSessionId
        : summary.latestStuckSessionId;

    if (!sessionId) {
      return;
    }

    setSelectedSessionId(sessionId);
    window.requestAnimationFrame(() =>
      scrollElementIntoView(SESSION_DETAIL_ELEMENT_ID, 'start'),
    );
  };

  const handleCauseEventJump = (eventIndex: number) => {
    focusJourneyElement({ target: 'event', index: eventIndex });
  };

  const handleScreenJump = (screenIndex: number) => {
    focusJourneyElement({ target: 'screen', index: screenIndex });
  };

  const handleDetailRetry = () => {
    if (!activeSessionId) {
      return;
    }

    setFailedDetailIds((previousFailures) => {
      const { [activeSessionId]: _removedFailure, ...restFailures } =
        previousFailures;

      return restFailures;
    });
  };

  if (!isRootAdmin) {
    return null;
  }

  return (
    <TimelineSection
      loading={loading}
      loadError={loadError}
      onlyProblems={onlyProblems}
      techMode={techMode}
      studentId={studentId ?? null}
      detailLoading={Boolean(activeSession && !activeDetail && !isDetailFailed)}
      detailError={isDetailFailed}
      truncated={sessionsResponse.truncated}
      platform={platform}
      studentEmail={studentEmail}
      totalSessions={sessionsResponse.sessions.length}
      visibleSessionsCount={visibleSessions.length}
      selectedSessionId={activeSessionId}
      selectedSession={activeSession}
      detail={activeDetail}
      highlight={highlight}
      summary={summary}
      studentStory={studentStory}
      platformTabs={platformTabs}
      sessionGroups={sessionGroups}
      exitScreenStats={exitScreenStats}
      platformSessionsCount={platformSessions.length}
      onPlatformChange={handlePlatformChange}
      onOnlyProblemsChange={handleOnlyProblemsChange}
      onTechModeChange={handleTechModeChange}
      onSessionSelect={handleSessionSelect}
      onCauseSessionShow={handleCauseSessionShow}
      onCauseEventJump={handleCauseEventJump}
      onScreenJump={handleScreenJump}
      onDetailRetry={handleDetailRetry}
    />
  );
};

export default React.memo(TimelineSectionContainer) as any;
